import type { ElementType } from 'react';
import type { DocumentType } from '@/lib/reports';
import {
  Building2,
  Factory,
  Handshake,
  HardHat,
  Store,
  UserCheck,
} from 'lucide-react';
import type { WorkItem } from '@/components/UniversalWorkItems';
import { buildStakeholderEntityWorkItemId, parseStakeholderEntityWorkItemId } from '@/lib/contacts/stakeholderWorkItemIdentity';
import { getWorkItemTypeIdentity } from '@/lib/work-items/typeIdentity';

export type StakeholderType = 'company' | 'customer' | 'vendor' | 'supplier' | 'partner' | 'contractor' | 'other';
export type StakeholderEntityKind = 'contact' | 'person' | 'location' | 'document';

type StakeholderFinancePreviewParams = {
  contactId: number | null;
  linkedAccountId: number | null;
  dateFrom: string;
  dateTo: string;
};

export interface StakeholderFinancePreviewOption {
  id: string;
  label: string;
  documentType: DocumentType;
  action: string;
  scope: 'contact' | 'client';
  requiresContactId?: boolean;
  requiresLinkedAccountId?: boolean;
  buildParams: (params: StakeholderFinancePreviewParams) => Record<string, unknown>;
}

type StakeholderFinancePreviewProfile = 'customer' | 'supplier' | 'vendor' | 'general';

export interface StakeholderTheme {
  label: string;
  primaryColor: string;
  backgroundClassName: string;
  badgeClassName: string;
  icon: ElementType;
}

export interface StakeholderContactLike {
  id: number;
  company_name: string | null;
  contact_person?: string | null;
  contact_number?: string | null;
  contact_type: StakeholderType;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  website?: string | null;
  tax_number?: string | null;
  registration_number?: string | null;
  payment_terms?: string | null;
  payment_terms_other?: string | null;
  payment_terms_days?: number | null;
  credit_limit?: number | null;
  current_balance?: number | null;
  account_status?: string | null;
  currency?: string | null;
  is_active?: boolean;
  notes?: string | null;
  logo_url?: string | null;
  photo_url?: string | null;
  legal_name?: string | null;
  legal_address?: string | null;
  trading_name?: string | null;
  industry?: string | null;
  company_size?: string | null;
  bank_name?: string | null;
  bank_account_number?: string | null;
  bank_branch_code?: string | null;
  bank_account_type?: string | null;
  bee_level?: string | null;
  bee_expiry_date?: string | null;
  insurance_provider?: string | null;
  insurance_policy_number?: string | null;
  insurance_expiry_date?: string | null;
  license_number?: string | null;
  license_expiry_date?: string | null;
  hourly_rate?: number | null;
  daily_rate?: number | null;
  lead_time_days?: number | null;
  minimum_order_value?: number | null;
  discount_percentage?: number | null;
  preferred_payment_method?: string | null;
  people_count?: number;
  location_count?: number;
  document_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface StakeholderPersonLike {
  id: number;
  contact_id: number;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  job_title?: string | null;
  department?: string | null;
  is_primary?: boolean;
  is_active?: boolean;
  notes?: string | null;
  default_location_id?: number | null;
  default_location_name?: string | null;
  contact_company_name?: string | null;
  photo_url?: string | null;
  created_at?: string;
  updated_at?: string;
  assigned_locations?: Array<{
    location_id: number;
    location_name: string;
    city?: string;
    role_at_location?: string;
    is_primary?: boolean;
  }>;
}

export interface StakeholderLocationLike {
  id: number;
  contact_id: number;
  contact_person_id?: number | null;
  location_name: string;
  location_type?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  radius_meters?: number;
  is_primary?: boolean;
  is_billing?: boolean;
  is_shipping?: boolean;
  is_active?: boolean;
  notes?: string | null;
  contact_name?: string | null;
  contact_company_name?: string | null;
  contact_person?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface StakeholderDocumentLike {
  id: number;
  contact_id: number;
  location_id?: number | null;
  contact_person_id?: number | null;
  document_type?: string | null;
  document_name: string;
  description?: string | null;
  file_url?: string | null;
  file_size?: number | null;
  mime_type?: string | null;
  issue_date?: string | null;
  expiry_date?: string | null;
  is_expired?: boolean;
  is_verified?: boolean;
  location_name?: string | null;
  person_name?: string | null;
  contact_company_name?: string | null;
  created_at?: string;
  updated_at?: string;
}

const DEFAULT_DATE = new Date(0).toISOString();

function buildStakeholderSyntheticUuid(kind: StakeholderEntityKind, entityId: number): string {
  return buildStakeholderEntityWorkItemId(kind, entityId);
}

function buildStakeholderTheme(
  type: StakeholderType,
  label: string,
  icon: ElementType,
): StakeholderTheme {
  const identity = getWorkItemTypeIdentity(type);

  return {
    label,
    primaryColor: identity.color,
    backgroundClassName: identity.gradient ? `bg-gradient-to-r ${identity.gradient}` : '',
    badgeClassName: identity.badge,
    icon,
  };
}

export const STAKEHOLDER_THEMES: Record<StakeholderType, StakeholderTheme> = {
  customer: buildStakeholderTheme('customer', 'Customer', UserCheck),
  supplier: buildStakeholderTheme('supplier', 'Supplier', Factory),
  vendor: buildStakeholderTheme('vendor', 'Vendor', Store),
  partner: buildStakeholderTheme('partner', 'Partner', Handshake),
  contractor: buildStakeholderTheme('contractor', 'Contractor', HardHat),
  company: buildStakeholderTheme('company', 'My Company', Building2),
  other: buildStakeholderTheme('other', 'Stakeholder', Building2),
};

export function getStakeholderTheme(type?: string | null): StakeholderTheme {
  const normalized = (type || 'other') as StakeholderType;
  return STAKEHOLDER_THEMES[normalized] || STAKEHOLDER_THEMES.other;
}

export function getStakeholderModalLabel(kind?: StakeholderEntityKind | null): string {
  switch (kind) {
    case 'contact':
      return 'Stakeholder';
    case 'person':
      return 'Person';
    case 'location':
      return 'Location';
    case 'document':
      return 'Document';
    default:
      return 'Stakeholder';
  }
}

export function isStakeholderWorkItem(workItem?: WorkItem | null): boolean {
  const customFields = getStakeholderCustomFields(workItem);
  const parsedIdentity = parseStakeholderEntityWorkItemId(String(workItem?.id || ''));

  return Boolean(customFields.stakeholder_entity_kind || customFields.stakeholder_entity_type || customFields.is_virtual_stakeholder || parsedIdentity);
}

function getStakeholderCustomFields(workItem?: WorkItem | null): Record<string, unknown> {
  if (!workItem?.custom_fields) {
    return {};
  }

  if (typeof workItem.custom_fields === 'object' && workItem.custom_fields !== null && !Array.isArray(workItem.custom_fields)) {
    return workItem.custom_fields as Record<string, unknown>;
  }

  if (typeof workItem.custom_fields === 'string') {
    try {
      const parsed = JSON.parse(workItem.custom_fields);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
  }

  return {};
}

function toPositiveInteger(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.trunc(value);
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
}

export function getStakeholderContactId(workItem?: WorkItem | null): number | null {
  const customFields = getStakeholderCustomFields(workItem);
  const parsedIdentity = parseStakeholderEntityWorkItemId(String(workItem?.id || ''));
  const entityKind = typeof customFields.stakeholder_entity_kind === 'string'
    ? customFields.stakeholder_entity_kind
    : typeof customFields.stakeholder_entity_type === 'string'
      ? customFields.stakeholder_entity_type
      : parsedIdentity?.kind ?? null;
  const parentContact = typeof customFields.parent_contact === 'object' && customFields.parent_contact !== null
    ? customFields.parent_contact as { id?: number | string | null }
    : null;
  const stakeholderEntity = typeof customFields.stakeholder_entity === 'object'
    && customFields.stakeholder_entity !== null
    && !Array.isArray(customFields.stakeholder_entity)
    ? customFields.stakeholder_entity as { id?: unknown; contact_id?: unknown }
    : null;

  const contactRootFromEntity = entityKind === 'contact'
    ? toPositiveInteger(stakeholderEntity?.id)
    : null;
  // Synthetic work item ids encode the real entity id; prefer parsing the id string over embedded
  // custom_fields objects, which can be stale after partial updates from tabs.
  const resolvedFromParsedContact = parsedIdentity?.kind === 'contact' ? parsedIdentity.entityId : null;
  const entityOwnContactFk = entityKind !== 'contact'
    ? toPositiveInteger(stakeholderEntity?.contact_id)
    : null;

  return [
    resolvedFromParsedContact,
    contactRootFromEntity,
    entityOwnContactFk,
    toPositiveInteger(parentContact?.id),
    toPositiveInteger(customFields.parent_contact_id),
    entityKind === 'contact' ? toPositiveInteger(customFields.entity_id) : null,
    toPositiveInteger(workItem?.contact_id),
  ].find((value): value is number => typeof value === 'number' && value > 0) ?? null;
}

/**
 * DELETE URL for a virtual stakeholder work item (contacts API), or null if deletion should use work-items.
 */
export function resolveVirtualStakeholderRecordDelete(workItem?: WorkItem | null): { url: string } | null {
  if (!workItem) return null;

  const fields = getStakeholderCustomFields(workItem);
  const parsedIdentity = parseStakeholderEntityWorkItemId(String(workItem.id || ''));
  const isVirtual = Boolean(fields.is_virtual_stakeholder) || parsedIdentity !== null;
  if (!isVirtual) return null;

  const entityKind = typeof fields.stakeholder_entity_kind === 'string'
    ? fields.stakeholder_entity_kind
    : typeof fields.stakeholder_entity_type === 'string'
      ? fields.stakeholder_entity_type
      : parsedIdentity?.kind ?? null;

  const stakeholderEntity = typeof fields.stakeholder_entity === 'object'
    && fields.stakeholder_entity !== null
    && !Array.isArray(fields.stakeholder_entity)
    ? fields.stakeholder_entity as { id?: unknown }
    : null;

  const entityNumericId = parsedIdentity?.entityId ?? toPositiveInteger(stakeholderEntity?.id);

  if (entityKind === 'contact') {
    const contactId = getStakeholderContactId(workItem);
    return contactId ? { url: `/api/v1/contacts/${contactId}` } : null;
  }

  if (!entityNumericId || !entityKind) return null;

  if (entityKind === 'person') {
    return { url: `/api/v1/contacts/people/${entityNumericId}` };
  }

  if (entityKind === 'location') {
    return { url: `/api/v1/contacts/locations/${entityNumericId}` };
  }

  if (entityKind === 'document') {
    return { url: `/api/v1/contacts/documents/${entityNumericId}` };
  }

  return null;
}

export function getStakeholderLinkedAccountId(workItem?: WorkItem | null): number | null {
  const customFields = getStakeholderCustomFields(workItem);

  return [
    customFields.account_id,
    customFields.linked_account_id,
    customFields.receivables_account_id,
    customFields.debtor_account_id,
    customFields.creditor_account_id,
    customFields.gl_account_id,
    customFields.ledger_account_id,
  ].map(toPositiveInteger).find((value): value is number => typeof value === 'number' && value > 0) ?? null;
}

const COMMON_STAKEHOLDER_FINANCE_OPTIONS: StakeholderFinancePreviewOption[] = [
  {
    id: 'account_transactions',
    label: 'Account Transactions',
    documentType: 'account_transactions',
    action: 'generateAccountTransactions',
    scope: 'contact',
    requiresLinkedAccountId: true,
    buildParams: ({ linkedAccountId, dateFrom, dateTo }) => ({
      account_id: linkedAccountId,
      date_from: dateFrom,
      date_to: dateTo,
    }),
  },
  {
    id: 'journal_report',
    label: 'Account Journals',
    documentType: 'journal_report',
    action: 'generateJournalReport',
    scope: 'client',
    buildParams: ({ dateFrom, dateTo }) => ({
      date_from: dateFrom,
      date_to: dateTo,
    }),
  },
];

const CUSTOMER_FINANCE_OPTIONS: StakeholderFinancePreviewOption[] = [
  {
    id: 'customer_statement',
    label: 'Customer Statement',
    documentType: 'customer_statement',
    action: 'generateCustomerStatement',
    scope: 'contact',
    requiresContactId: true,
    buildParams: ({ contactId, dateFrom, dateTo }) => ({
      contact_id: contactId,
      date_from: dateFrom,
      date_to: dateTo,
    }),
  },
  ...COMMON_STAKEHOLDER_FINANCE_OPTIONS,
];

const SUPPLIER_FINANCE_OPTIONS: StakeholderFinancePreviewOption[] = [
  {
    id: 'aged_payables',
    label: 'Aged Payables',
    documentType: 'aged_payables',
    action: 'generateAgedPayables',
    scope: 'client',
    buildParams: ({ dateTo }) => ({
      as_of_date: dateTo,
    }),
  },
  ...COMMON_STAKEHOLDER_FINANCE_OPTIONS,
];

function getStakeholderFinancePreviewProfile(workItem?: WorkItem | null): StakeholderFinancePreviewProfile {
  const customFields = getStakeholderCustomFields(workItem);
  const stakeholderType = String(customFields.stakeholder_type || workItem?.item_type || 'other').toLowerCase();

  if (stakeholderType === 'customer') {
    return 'customer';
  }

  if (stakeholderType === 'supplier') {
    return 'supplier';
  }

  if (stakeholderType === 'vendor') {
    return 'vendor';
  }

  return 'general';
}

function getStakeholderFinancePreviewPriority(profile: StakeholderFinancePreviewProfile): string[] {
  switch (profile) {
    case 'customer':
      return ['customer_statement', 'account_transactions', 'journal_report'];
    case 'supplier':
    case 'vendor':
      return ['aged_payables', 'account_transactions', 'journal_report'];
    default:
      return ['account_transactions', 'journal_report'];
  }
}

export function getStakeholderFinancePreviewPreferenceKey(workItem?: WorkItem | null): string {
  return `ta_stakeholder_finance_preview_report:${getStakeholderFinancePreviewProfile(workItem)}`;
}

export function getStakeholderFinanceTemplatePreferenceNamespace(workItem?: WorkItem | null): string {
  return `ta_stakeholder_finance_preview_template:${getStakeholderFinancePreviewProfile(workItem)}`;
}

export function getStakeholderFinancePreviewOptions(workItem?: WorkItem | null): StakeholderFinancePreviewOption[] {
  if (!workItem || !isStakeholderWorkItem(workItem)) {
    return [];
  }

  const customFields = getStakeholderCustomFields(workItem);
  const parsedIdentity = parseStakeholderEntityWorkItemId(String(workItem.id || ''));
  const entityKind = typeof customFields.stakeholder_entity_kind === 'string'
    ? customFields.stakeholder_entity_kind
    : typeof customFields.stakeholder_entity_type === 'string'
      ? customFields.stakeholder_entity_type
      : parsedIdentity?.kind ?? null;

  if (entityKind !== 'contact') {
    return [];
  }

  const financeProfile = getStakeholderFinancePreviewProfile(workItem);
  const contactId = getStakeholderContactId(workItem);
  const linkedAccountId = getStakeholderLinkedAccountId(workItem);
  const options = financeProfile === 'customer'
    ? CUSTOMER_FINANCE_OPTIONS
    : financeProfile === 'supplier' || financeProfile === 'vendor'
      ? SUPPLIER_FINANCE_OPTIONS
      : COMMON_STAKEHOLDER_FINANCE_OPTIONS;

  const availableOptions = options.filter((option) => {
    if (option.requiresContactId && !contactId) {
      return false;
    }

    if (option.requiresLinkedAccountId && !linkedAccountId) {
      return false;
    }

    return true;
  });

  const priorityOrder = getStakeholderFinancePreviewPriority(financeProfile);

  return [...availableOptions].sort((left, right) => {
    const leftPriority = priorityOrder.indexOf(left.id);
    const rightPriority = priorityOrder.indexOf(right.id);

    if (leftPriority === -1 && rightPriority === -1) {
      return left.label.localeCompare(right.label);
    }

    if (leftPriority === -1) {
      return 1;
    }

    if (rightPriority === -1) {
      return -1;
    }

    return leftPriority - rightPriority;
  });
}

export function getPreferredStakeholderFinancePreviewOptionId(
  workItem?: WorkItem | null,
  options?: StakeholderFinancePreviewOption[],
): string | null {
  const availableOptions = options ?? getStakeholderFinancePreviewOptions(workItem);

  if (availableOptions.length === 0) {
    return null;
  }

  const priorityOrder = getStakeholderFinancePreviewPriority(getStakeholderFinancePreviewProfile(workItem));
  const preferredOptionId = priorityOrder.find((optionId) => availableOptions.some((option) => option.id === optionId));

  return preferredOptionId ?? availableOptions[0]?.id ?? null;
}

function buildBaseStakeholderWorkItem(params: {
  id: string;
  itemNumber: string;
  itemType: StakeholderType;
  itemSubtype: string;
  title: string;
  description?: string;
  status: string;
  contactId?: number | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  locationId?: number | null;
  locationName?: string | null;
  contactPersonId?: number | null;
  contactPerson?: string | null;
  createdAt?: string;
  updatedAt?: string;
  tags?: string[];
  customFields: Record<string, unknown>;
}): WorkItem {
  return {
    id: params.id,
    item_number: params.itemNumber,
    item_type: params.itemType,
    item_subtype: params.itemSubtype,
    title: params.title,
    description: params.description,
    status: params.status,
    priority: 'medium',
    created_by: 0,
    assigned_to: null,
    assignees: [],
    location_id: params.locationId ?? null,
    location_name: params.locationName ?? null,
    contact_id: params.contactId ?? null,
    contact_name: params.contactName ?? null,
    contact_email: params.contactEmail ?? null,
    contact_phone: params.contactPhone ?? null,
    contact_person_id: params.contactPersonId ?? null,
    contact_person: params.contactPerson ?? null,
    progress_percentage: 0,
    tags: params.tags || [],
    custom_fields: params.customFields,
    created_at: params.createdAt || DEFAULT_DATE,
    updated_at: params.updatedAt || params.createdAt || DEFAULT_DATE,
  };
}

export function buildStakeholderContactWorkItem(contact: StakeholderContactLike): WorkItem {
  const theme = getStakeholderTheme(contact.contact_type);
  const title = contact.company_name || contact.contact_person || `Contact #${contact.id}`;

  // Surface the contact's account_status as the work item status so the shared
  // StatusTransition / DetailsSidebar UIs treat it as a workflow status. Falls
  // back to is_active when account_status isn't set.
  const rawAccountStatus = (contact as { account_status?: string | null }).account_status;
  const status = (typeof rawAccountStatus === 'string' && rawAccountStatus.trim())
    ? rawAccountStatus.trim()
    : (contact.is_active === false ? 'inactive' : 'active');

  return buildBaseStakeholderWorkItem({
    id: buildStakeholderSyntheticUuid('contact', contact.id),
    itemNumber: `C-${contact.id}`,
    itemType: contact.contact_type || 'other',
    itemSubtype: 'stakeholder_contact',
    title,
    description: [contact.industry, contact.email, contact.phone || contact.mobile, contact.notes].filter(Boolean).join(' • '),
    status,
    contactId: contact.id,
    contactName: title,
    contactEmail: contact.email || null,
    contactPhone: contact.phone || contact.mobile || null,
    createdAt: contact.created_at,
    updatedAt: contact.updated_at,
    tags: [contact.trading_name, contact.legal_name, contact.industry].filter((tag): tag is string => Boolean(tag && tag.trim())),
    customFields: {
      is_virtual_stakeholder: true,
      stakeholder_entity_kind: 'contact',
      stakeholder_type: contact.contact_type || 'other',
      stakeholder_theme: theme,
      stakeholder_modal_label: getStakeholderModalLabel('contact'),
      entity_id: contact.id,
      stakeholder_contact: contact,
      // Financial / account-standing fields surfaced for the overview tab and
      // the account-warning helper.
      account_status: status,
      credit_limit: (contact as { credit_limit?: number | null }).credit_limit ?? null,
      current_balance: (contact as { current_balance?: number | null }).current_balance ?? null,
      currency: contact.currency ?? null,
      payment_terms: contact.payment_terms ?? null,
      payment_terms_other: contact.payment_terms_other ?? null,
      payment_terms_days: (contact as { payment_terms_days?: number | null }).payment_terms_days ?? null,
    },
  });
}

export function buildStakeholderPersonWorkItem(
  person: StakeholderPersonLike,
  stakeholderType: StakeholderType = 'other'
): WorkItem {
  const theme = getStakeholderTheme(stakeholderType);
  const title = `${person.first_name || ''} ${person.last_name || ''}`.trim() || `Person #${person.id}`;

  return buildBaseStakeholderWorkItem({
    id: buildStakeholderSyntheticUuid('person', person.id),
    itemNumber: `P-${person.id}`,
    itemType: stakeholderType,
    itemSubtype: 'stakeholder_person',
    title,
    description: [person.job_title, person.department, person.email, person.phone || person.mobile, person.notes].filter(Boolean).join(' • '),
    status: person.is_active === false ? 'inactive' : 'active',
    contactId: person.contact_id,
    contactName: person.contact_company_name || null,
    contactEmail: person.email || null,
    contactPhone: person.phone || person.mobile || null,
    contactPersonId: person.id,
    contactPerson: title,
    createdAt: person.created_at,
    updatedAt: person.updated_at || person.created_at,
    tags: [person.department, person.job_title, person.default_location_name].filter((tag): tag is string => Boolean(tag && tag.trim())),
    customFields: {
      is_virtual_stakeholder: true,
      stakeholder_entity_kind: 'person',
      stakeholder_type: stakeholderType,
      stakeholder_theme: theme,
      stakeholder_modal_label: getStakeholderModalLabel('person'),
      entity_id: person.id,
      parent_contact_id: person.contact_id,
      stakeholder_person: person,
    },
  });
}

export function buildStakeholderLocationWorkItem(
  location: StakeholderLocationLike,
  stakeholderType: StakeholderType = 'other'
): WorkItem {
  const theme = getStakeholderTheme(stakeholderType);
  const addressParts = [location.address_line1, location.address_line2, location.city, location.state, location.postal_code, location.country]
    .filter(Boolean)
    .join(', ');

  return buildBaseStakeholderWorkItem({
    id: buildStakeholderSyntheticUuid('location', location.id),
    itemNumber: `L-${location.id}`,
    itemType: stakeholderType,
    itemSubtype: 'stakeholder_location',
    title: location.location_name,
    description: [addressParts, location.notes].filter(Boolean).join(' • '),
    status: location.is_active === false ? 'inactive' : 'active',
    contactId: location.contact_id,
    contactName: location.contact_name || location.contact_company_name || null,
    contactEmail: location.contact_email || null,
    contactPhone: location.contact_phone || null,
    locationId: location.id,
    locationName: location.location_name,
    contactPersonId: location.contact_person_id ?? null,
    contactPerson: location.contact_person || null,
    createdAt: location.created_at,
    updatedAt: location.updated_at || location.created_at,
    tags: [location.location_type, location.city, location.state, location.country].filter((tag): tag is string => Boolean(tag && tag.trim())),
    customFields: {
      is_virtual_stakeholder: true,
      stakeholder_entity_kind: 'location',
      stakeholder_type: stakeholderType,
      stakeholder_theme: theme,
      stakeholder_modal_label: getStakeholderModalLabel('location'),
      entity_id: location.id,
      parent_contact_id: location.contact_id,
      stakeholder_location: location,
    },
  });
}

export function buildStakeholderDocumentWorkItem(
  document: StakeholderDocumentLike,
  stakeholderType: StakeholderType = 'other'
): WorkItem {
  const theme = getStakeholderTheme(stakeholderType);

  return buildBaseStakeholderWorkItem({
    id: buildStakeholderSyntheticUuid('document', document.id),
    itemNumber: `D-${document.id}`,
    itemType: stakeholderType,
    itemSubtype: 'stakeholder_document',
    title: document.document_name,
    description: [document.document_type, document.description, document.location_name, document.person_name].filter(Boolean).join(' • '),
    status: document.is_expired ? 'expired' : 'active',
    contactId: document.contact_id,
    contactName: document.contact_company_name || null,
    locationId: document.location_id ?? null,
    locationName: document.location_name || null,
    contactPersonId: document.contact_person_id ?? null,
    contactPerson: document.person_name || null,
    createdAt: document.created_at,
    updatedAt: document.updated_at || document.created_at,
    tags: [document.document_type, document.location_name, document.person_name].filter((tag): tag is string => Boolean(tag && tag.trim())),
    customFields: {
      is_virtual_stakeholder: true,
      stakeholder_entity_kind: 'document',
      stakeholder_type: stakeholderType,
      stakeholder_theme: theme,
      stakeholder_modal_label: getStakeholderModalLabel('document'),
      entity_id: document.id,
      parent_contact_id: document.contact_id,
      stakeholder_document: document,
    },
  });
}
